import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, MessageCircle, ShieldCheck, X } from 'lucide-react';
import { useSubscription } from '../lib/hooks/useSubscription';
import { useAuth } from '../lib/auth';

/**
 * Fine del periodo di prova.
 *
 * Stessa impaginazione di PastDueBlockedModal e AddonFeatureLockModal: sono
 * tutti e tre momenti in cui si chiede di pagare, e devono somigliarsi. Il
 * tono resta calmo — qui non è successo niente di male, il cliente ha finito
 * la prova — quindi niente rosso da errore di sistema.
 *
 * I titoli portano un colore esplicito: la regola base su h1-h6 in index.css
 * vince sul colore ereditato dal contenitore, e un titolo che conta sul bianco
 * del genitore finisce grigio scuro su fondo colorato.
 */
export function TrialExpiredModal() {
  const subscription = useSubscription();
  const { isSuperAdmin } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed && isSuperAdmin) return null;
  if (subscription.loading || !subscription.organization) return null;
  if (subscription.organization.subscription_status === 'active') return null;
  if (!subscription.organization.subscription_plan_id || !subscription.plan) return null;

  const trialDays = subscription.trialDaysRemaining;
  const isExpired = trialDays <= 0 && subscription.organization.subscription_status === 'trial';
  if (!isExpired) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#37352F]/50 backdrop-blur-sm"
      >
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label="Periodo di prova terminato"
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-[440px] rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-gray-700 overflow-hidden"
        >
          {isSuperAdmin && (
            <button
              onClick={() => setDismissed(true)}
              title="Chiudi (solo superadmin)"
              className="absolute top-4 right-4 z-10 p-1.5 rounded-lg text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-100 hover:bg-[#F7F6F3] dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          <div className="px-7 pt-8 pb-6">
            <div className="w-11 h-11 rounded-xl bg-[#E91E8C]/10 flex items-center justify-center mb-5">
              <Crown className="w-5 h-5 text-[#E91E8C]" strokeWidth={2} />
            </div>

            <h2 className="text-[21px] font-bold text-[#37352F] dark:text-gray-100 leading-tight tracking-[-0.01em] mb-2">
              La prova è finita
            </h2>
            <p className="text-[14px] text-[#787774] dark:text-gray-400 leading-relaxed mb-5">
              Hai usato Flusia per {subscription.plan?.trial_days || 30} giorni con il piano{' '}
              {subscription.plan?.name || 'di prova'}. Per continuare da dove eri rimasto, scegli
              un abbonamento.
            </p>

            <ul className="space-y-2 mb-6">
              {[
                'Lead, clienti e offerte: tutto intatto',
                'Nessuna configurazione da rifare',
                'Cambi o disdici il piano quando vuoi',
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 text-[13px] text-[#37352F] dark:text-gray-200"
                >
                  <ShieldCheck className="w-4 h-4 text-[#E91E8C] flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => { window.location.href = '/settings?tab=subscription'; }}
              className="w-full py-3 rounded-xl bg-[#E91E8C] hover:bg-[#C9187A] text-white text-[14px] font-semibold transition-colors"
            >
              Scegli un abbonamento
            </button>

            <a
              href="https://wa.me/393932787927?text=Ciao%2C%20mi%20%C3%A8%20scaduta%20la%20prova%20di%20Flusia%20e%20vorrei%20un%20aiuto%20a%20scegliere%20il%20piano"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full py-2.5 rounded-xl text-[13px] font-medium text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-100 hover:bg-[#F7F6F3] dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-1.5"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Non sai quale scegliere? Scrivici su WhatsApp
            </a>
          </div>

          <div className="px-7 py-3.5 bg-[#F7F6F3] dark:bg-gray-900/40 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700">
            <p className="text-center text-[11px] text-[#787774] dark:text-gray-400">
              Il tuo account e tutti i tuoi dati restano al sicuro.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
